import type { CompetitorOverview } from "@/lib/api/types";

import { formatCount, formatDateTime } from "./format";

type Competitor = NonNullable<CompetitorOverview["competitors"]>["competitors"][number];
type Gap = NonNullable<CompetitorOverview["gap"]>;

// Spiegel der Schätzung im Backend (`modules/competitors/engine.py`): Obergrenzen in ahrefs-Units.
// Die Rückfrage rechnet damit schon beim Ändern der Auswahl, das Backend nennt beim Start dieselbe Zahl.
const MIN_UNITS = 50; // Mindestkosten je Abruf
const UNITS_KEYWORD_ROW = 15; // Keyword-Zeile mit Suchvolumen
const UNITS_OWN_ROW = 4;
const OWN_BATCH = 40;

/** Obergrenze der ahrefs-Units für die Content-Gap: ein Abruf je Wettbewerber, dazu die eigenen Rankings in Teilen. */
export function gapUnits(domains: number, perDomain: number): number {
  const keywords = domains * perDomain;
  const batches = keywords > 0 ? Math.ceil(keywords / OWN_BATCH) : 0;
  return domains * Math.max(MIN_UNITS, perDomain * UNITS_KEYWORD_ROW) + batches * Math.max(MIN_UNITS, OWN_BATCH * UNITS_OWN_ROW);
}

/** Erste Auswahl für die Content-Gap: die Vorauswahl des Backends, nur Domains der Liste. */
export function initialSelection(list: NonNullable<CompetitorOverview["competitors"]>, previous: string[]): string[] {
  const known = new Set(list.competitors.map((c) => c.domain));
  const kept = previous.filter((domain) => known.has(domain));
  return kept.length > 0 ? kept : list.preselected;
}

export function toggleDomain(selected: string[], domain: string, max: number): string[] {
  if (selected.includes(domain)) return selected.filter((d) => d !== domain);
  return selected.length >= max ? selected : [...selected, domain];
}

export function competitorNote(competitor: Competitor): string | null {
  if (competitor.generic) return "Plattform oder Verzeichnis";
  if (competitor.related) return "Eigene oder verwandte Marke";
  return null;
}

export function gapSummary(gap: Gap): string {
  return `${formatCount(gap.gap_keywords)} Lücken in ${formatCount(gap.group_count)} Themen`;
}

export const STATE_TEXT = { missing: "fehlt", weak: "schwach" } as const;

/** Eigene Position zum Keyword in Worten: Status nie nur über Farbe. */
export function ownPosition(state: "missing" | "weak", position: number | null): string {
  return state === "missing" || position === null ? "rankt nicht" : `Platz ${formatCount(position)} (schwach)`;
}

export function runLine(run: NonNullable<CompetitorOverview["competitors_run"]>): string {
  if (run.state === "running") return "läuft";
  if (run.state === "failed") return run.error ? `fehlgeschlagen: ${run.error}` : "fehlgeschlagen";
  return `vom ${formatDateTime(run.finished_at ?? run.created_at)}`;
}
